import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from 'src/components/ui/Features/Spinner/Spinner';
import { useScenario } from 'src/hook/data/useScenario';
import { LessonState } from 'src/model/Lessons';
import ScenarioItem from './ScenarioItem/ScenarioItem';
import styles from './ScenarioPage.module.scss';

type FilterType = 'outstanding' | 'completed';

const ScenarioPage = () => {
    const { getAllScenarios, isLoading, scenarios, scenarioQAs } = useScenario();
    const [lessons, setLessons] = useState<LessonState[]>([]);
    const [activeFilter, setActiveFilter] = useState<FilterType>('outstanding');
    const { t } = useTranslation();

    useEffect(() => {
        if (!scenarios || !scenarioQAs) return;

        const fetchLessons = async () => {
            try {
                const fetchedLessons = await getAllScenarios();
                if (fetchedLessons.length > 0) {
                    setLessons(fetchedLessons);
                }
            } catch (error) {
                console.error('Error fetching lessons:', error);
            }
        };

        fetchLessons();
    }, [scenarios, scenarioQAs, getAllScenarios]);

    const filteredLessons = lessons.filter(lesson =>
        activeFilter === 'outstanding' ? !lesson.IsCompleted : lesson.IsCompleted
    );

    // Group lessons by category
    const groupedLessons = filteredLessons.reduce((acc, lesson) => {
        if (!acc[lesson.Cat]) {
            acc[lesson.Cat] = [];
        }
        acc[lesson.Cat].push(lesson);
        return acc;
    }, {} as Record<string, LessonState[]>);

    if (isLoading || !scenarios || !scenarioQAs) {
        return <Spinner />
    }

    if (lessons.length === 0) {
        return null;
    }

    return (
        <article className={`${styles.scenarioPage}`}>
            <div className={styles.filterButtons}>
                <button
                    className={`${styles.filterButton} ${activeFilter === 'outstanding' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('outstanding')}
                >
                    {t('Outstanding')}
                </button>
                <button
                    className={`${styles.filterButton} ${activeFilter === 'completed' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('completed')}
                >
                    {t('Completed')}
                </button>
            </div>

            <div className={styles.scenarioGrid}>
                {Object.entries(groupedLessons).map(([category, categoryLessons]) => (
                    <div key={category}>
                        <h2 className={styles.categoryTitle}>{t(category)}</h2>
                        <div className={styles.categoryContainer}>
                            {categoryLessons.map((lesson) => (
                                <ScenarioItem key={lesson.QuestionId} lesson={lesson} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </article>
    );
}

export default ScenarioPage;